<template>
  <vf-page-loader v-if="loading || formRef?.loading" />
  <div v-if="!user.isWranxOrIpaProfile" :class="brandClasses.wrapper">
    <product-shipping-methods-option
      v-model="method"
      :description="$t.standardShippingTime"
      :disabled
      icon="shipping"
      :label="$t.shipToHome"
      :show-edd="$feature.showEddOnPDP"
      type="standard"
      :variant="defaultVariant"
    />
    <product-shipping-methods-option
      v-model="method"
      :description="pickupDescription"
      :disabled
      :error="selectedStore && !isBopis && !isSTS"
      icon="location"
      :label="$t.inStorePickup"
      :selected-store="selectedStore"
      type="pickup"
      @change="selectPickup"
      @open-search="openStoreSearch"
    />
    <dialog-bopis-store-search v-if="enableStoreSearchAsDialogOnPDP" :stores />
    <form-bopis-store-search
      v-else-if="showStoreSearchForm"
      ref="formRef"
      class="mt-2 bg-grey-90 p-4"
      initially-hidden
      show-close-button
      :stores
      @close="showStoreSearchForm = false"
    />
  </div>
</template>

<script lang="ts" setup>
import type { FormBopisStoreSearch as FormBopisStoreSearchType } from '#components'
import type { LocatorSearchItem } from '#types/brandify'
import type { ShippingMethodContext } from '#types/gtm'

const props = defineProps<{
  disabled?: boolean
  emitInvalidSelection?: boolean
}>()

const emit = defineEmits<{
  invalidSelection: []
}>()

const { products } = useApi()
const { brandClasses, dialogOptions } = useAppConfig().components.product.shippingMethods
const { enableStoreSearchAsDialogOnPDP } = useFeatureFlags()
const { $t, $gtm } = useNuxtApp()
const origin = useOrigin()
const { attributeSelectionFormRef, defaultVariant, error, selectedVariant } = usePdp()
const route = useRoute()
const storeFilter = useRouteQuery<string | undefined>('storeFilter')
const gtmTrace = useSessionStorage('gtmTrace', {} as Record<string, ShippingMethodContext>)
const user = useUserStore()

const { selectedStore, stores: _stores } = storeToRefs(useBopisStore())
const formRef = ref<InstanceType<typeof FormBopisStoreSearchType>>()
const loading = ref(false)
const method = ref<'standard' | 'pickup'>(storeFilter.value ? 'pickup' : 'standard')
const showStoreSearchForm = ref(false)

const DialogBopisStoreSearch = createDialog('bopis-store-search', dialogOptions)

const isBopis = computed(() => {
  if (!selectedStore.value) return
  return !!selectedStore.value.quantity && selectedStore.value.bopis_enabled === '1'
})

const isSTS = computed(() => {
  if (!selectedStore.value) return
  return selectedStore.value.sts_enabled === '1'
})

const pickupDescription = computed(() => {
  if (!selectedVariant.value) return $t.completeSelection
  else if (!selectedStore.value) return $t.selectStoreToSeeAvailability
  else return isBopis.value ? $t.readyIn24hours : isSTS.value ? $t.readyIn3Days : $t.productNotFoundInStore
})

const validateBopis = () => {
  error.value = !attributeSelectionFormRef.value?.validate(true)
  if (!error.value) return true
  if (props.emitInvalidSelection) emit('invalidSelection')
}

const updateAvailability = async (productId: string, _stores: LocatorSearchItem[]): Promise<LocatorSearchItem[]> => {
  try {
    const storeInventoryResponses = await products.$getStoreInventory(
      productId,
      _stores.map((store) => ({ storeId: store.storeid }))
    )

    return _stores.map((store) => {
      const availableStore = storeInventoryResponses.storeInventory?.find(
        (inventory) => inventory.storeId === store.storeid
      )
      return {
        ...store,
        quantity: availableStore?.quantity || 0
      }
    })
  }
  catch (error) {
    log.warn(error)
  }
  finally {
    loading.value = false
  }
  return _stores
}

const stores = computedAsync(() => _stores.value.length
  ? updateAvailability(selectedVariant.value?.id, _stores.value)
  : []
)

const openStoreSearch = () => {
  if (!validateBopis()) return

  $gtm.push('pdpPage.onFindInStore')

  if (enableStoreSearchAsDialogOnPDP) {
    DialogBopisStoreSearch.keep().open({ showSaveButton: true }).then(() => {
      method.value = selectedStore.value ? 'pickup' : 'standard'
    })
  }
  else {
    method.value = 'pickup'
    showStoreSearchForm.value = true
  }
}

const selectPickup = () => {
  if (!validateBopis()) {
    method.value = 'standard'
    return
  }
  if (!selectedStore.value) openStoreSearch()
}

watch([method, selectedStore, selectedVariant], ([newMethod], [oldMethod]) => {
  if (newMethod !== oldMethod) {
    const url = new URL(`${origin}${route.fullPath}`)
    const storeFilter = method.value === 'pickup' && selectedStore.value ? selectedStore.value.storeid : null

    if (storeFilter)
      url.searchParams.set('storeFilter', storeFilter)
    else
      url.searchParams.delete('storeFilter')

    history.replaceState(history.state, '', url.toString())
  }

  if (selectedVariant.value?.id) {
    gtmTrace.value[selectedVariant.value?.id] = {
      shippingMethod: method.value === 'standard' ? 'STH' : selectedStore.value && isBopis.value ? 'BOPIS_PDP_SD' : 'BOPIS_PDP_STS',
      shippingStoreId: method.value === 'pickup' ? selectedStore.value?.storeid : undefined,
      shippingWindow: method.value === 'standard' ? '3-5d' : selectedStore.value && isBopis.value ? '24Hrs' : '3-5d',
    }
  }
}, {
  immediate: true
})

onMounted(() => {
  watch(() => selectedVariant.value?.id, async (productId) => {
    if (productId && selectedStore.value) {
      const result = (await updateAvailability(productId, [selectedStore.value]))[0]
      selectedStore.value = {
        ...selectedStore.value,
        quantity: result?.quantity || 0
      }
    }
  }, { immediate: true })
})

defineExpose({
  method,
  openStoreSearch
})
</script>
